/**
 * Shoe sizes must be chosen, not assumed.
 *
 * Shopify always loads a product with its first available variant, so the first shoe size
 * would otherwise look selected. Until the shopper taps a size on this page, the size
 * buttons show no selection, the stock line is hidden, Add to bag (main and sticky) reads
 * "Select a size", and Add to bag or the express checkout buttons point to the sizes
 * instead of buying the default size. Colour stays preselected.
 *
 * State lives on the product's section (or the quick add dialog), which variant changes
 * do not replace, so a chosen size survives colour changes; the quick add dialog starts
 * again for each product it opens. Loaded on every page, since quick add appears on
 * collection, search and home pages. The variant behind the page is untouched: prices,
 * stock and the cart keep working as normal.
 */
const PENDING = 'data-sc-size-pending';
// On phones the quick add sheet opens while its product is still loading (quick-add.js)
const LOADING = 'data-sc-size-loading';
const SIZE_FIELDSET = '.variant-option--size-system';
const BUY_CONTROLS =
  'add-to-cart-component button[name="add"], .sticky-add-to-cart__button, shopify-accelerated-checkout, .shopify-payment-button';

/** @type {WeakMap<Node, string>} original button labels, restored once a size is chosen */
const originalLabels = new WeakMap();

/** @param {Element} picker */
const rootOf = (picker) => picker.closest('quick-add-dialog') || picker.closest('.shopify-section') || document.body;

/** @param {Element} root */
const storageKey = (root) => {
  const handle = root.querySelector('variant-picker')?.getAttribute('data-product-handle') || location.pathname;
  return `sc-size-chosen:${handle}`;
};

/** @param {Element} root */
function chosenEarlier(root) {
  if (root.closest('quick-add-dialog')) return false;
  if (!new URL(location.href).searchParams.has('variant')) return false;
  try {
    return sessionStorage.getItem(storageKey(root)) === '1';
  } catch {
    return false;
  }
}

/**
 * The text node or element that holds a buy button's visible label.
 * @param {Element} button
 * @returns {Node | null}
 */
function labelNodeOf(button) {
  const sticky = button.querySelector('.add-to-cart-text__content > span');
  if (sticky) return sticky;
  for (const node of button.childNodes) {
    if (node.nodeType === Node.TEXT_NODE && node.textContent.trim()) return node;
  }
  return null;
}

/** @param {Element} root */
function syncLabels(root) {
  const pending = root.hasAttribute(PENDING);
  const prompt = root.querySelector('[data-sc-select-label]')?.getAttribute('data-sc-select-label') || 'Select a size';

  for (const button of root.querySelectorAll('add-to-cart-component button[name="add"], .sticky-add-to-cart__button')) {
    const node = labelNodeOf(button);
    if (!node) continue;
    if (pending) {
      if (!originalLabels.has(node)) originalLabels.set(node, node.textContent);
      const wanted = node.nodeType === Node.TEXT_NODE ? ` ${prompt} ` : prompt;
      if (node.textContent !== wanted) node.textContent = wanted;
    } else if (originalLabels.has(node)) {
      node.textContent = originalLabels.get(node) ?? node.textContent;
      originalLabels.delete(node);
    }
  }
}

/** @param {Element} root */
function setPending(root) {
  root.setAttribute(PENDING, '');
  syncLabels(root);
}

/** @param {Element} root */
function setChosen(root) {
  if (!root.hasAttribute(PENDING)) return;
  root.removeAttribute(PENDING);
  root.querySelectorAll('.sc-size-hint').forEach((hint) => hint.remove());
  syncLabels(root);
  try {
    sessionStorage.setItem(storageKey(root), '1');
  } catch {
    // Storage can be unavailable (private mode); the choice still holds for this page view.
  }
}

/** @param {Element} root */
function pointToSizes(root) {
  const fieldset = root.querySelector(SIZE_FIELDSET);
  if (!fieldset) return;

  if (!fieldset.querySelector('.sc-size-hint')) {
    const hint = document.createElement('p');
    hint.className = 'sc-size-hint';
    hint.setAttribute('role', 'alert');
    hint.textContent = root.querySelector('[data-sc-hint]')?.getAttribute('data-sc-hint') || 'Please select your size';
    fieldset.querySelector('.sc-size-system')?.after(hint);
  }

  fieldset.classList.remove('sc-size-attention');
  void fieldset.offsetWidth;
  fieldset.classList.add('sc-size-attention');
  fieldset.scrollIntoView({ block: 'center', behavior: 'smooth' });
  fieldset.querySelector('input')?.focus({ preventScroll: true });
}

/** @param {Element} picker */
function adopt(picker) {
  if (!picker.querySelector(SIZE_FIELDSET)) return;
  const root = rootOf(picker);
  // A quick add sheet that opened before its product arrived starts without a size once it does
  const arrivedLate = root.hasAttribute(LOADING);
  root.removeAttribute(LOADING);
  if (root.hasAttribute('data-sc-size-tracked')) {
    if (arrivedLate) setPending(root);
    else syncLabels(root);
    return;
  }
  root.setAttribute('data-sc-size-tracked', '');
  if (arrivedLate || !chosenEarlier(root)) setPending(root);

  // Variant changes re-render parts of the form; keep the prompt in place while no size is chosen
  let queued = false;
  new MutationObserver(() => {
    if (queued || !root.hasAttribute(PENDING)) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      syncLabels(root);
    });
  }).observe(root, { childList: true, subtree: true, characterData: true });
}

// A tap on a size button (or a size guide row, which taps the button) counts as a choice.
// Listen on window (capture): Horizon's own on:click handlers sit on document in the capture
// phase and are registered first, so a document listener would run after its add-to-cart
// animation; window capture runs before anything on document.
window.addEventListener(
  'click',
  (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const root = target.closest(`[${PENDING}]`);
    if (!root) return;

    if (target.closest(`${SIZE_FIELDSET} .variant-option__button-label, ${SIZE_FIELDSET} input`)) {
      setChosen(root);
      return;
    }

    if (target.closest(BUY_CONTROLS)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      pointToSizes(root);
    }
  },
  true
);

document.querySelectorAll('variant-picker').forEach(adopt);

// The quick add sheet reuses one dialog for every product: each time it opens, the product
// shown starts again without a size. (dialog:open does not bubble, so listen while capturing.)
document.addEventListener(
  'dialog:open',
  (event) => {
    const dialog = event.target instanceof Element ? event.target : null;
    if (!dialog || dialog.localName !== 'quick-add-dialog') return;
    const picker = dialog.querySelector('variant-picker');
    dialog.toggleAttribute(LOADING, !picker);
    if (!picker || !picker.querySelector(SIZE_FIELDSET)) {
      dialog.removeAttribute(PENDING);
      return;
    }
    adopt(picker);
    dialog.querySelectorAll('.sc-size-hint').forEach((hint) => hint.remove());
    setPending(dialog);
  },
  true
);

// Quick add dialogs and re-rendered sections bring in new pickers
new MutationObserver((records) => {
  for (const record of records) {
    for (const node of record.addedNodes) {
      if (!(node instanceof Element)) continue;
      if (node.matches('variant-picker')) adopt(node);
      node.querySelectorAll?.('variant-picker').forEach(adopt);
    }
  }
}).observe(document.body, { childList: true, subtree: true });
